import React from 'react';
import { ColumnValidationResult } from 'src/utils/CommonHelpers/sharedCSVUtils';
import './ColumnValidationCard.scss';

interface ColumnValidationCardProps {
  columnValidation: ColumnValidationResult;
  hasRowErrors: boolean;
  requiredColumns: string[];
  optionalColumns?: string[];
  optionalMissingColumns?: string[];
}

const ColumnValidationCard = ({
  columnValidation,
  hasRowErrors,
  requiredColumns,
  optionalColumns = [],
  optionalMissingColumns = [],
}: ColumnValidationCardProps) => {
  const { found, missing } = columnValidation;

  const columnsValid: boolean = missing.length === 0;
  const hasOptionalWarnings: boolean = optionalMissingColumns.length > 0;

  const getSubtitle = () => {
    if (!columnsValid) {
      return "Some required columns couldn't be read — fix the headers and re-upload";
    }

    if (hasRowErrors) {
      return "All required columns were found in the CSV, but some row values don't match the system";
    }

    if (hasOptionalWarnings) {
      return 'All required columns found — some optional columns were omitted (existing data will be preserved)';
    }

    return 'All required columns found — ready to import';
  };

  const cardStatus = !columnsValid || hasRowErrors
    ? 'error'
    : hasOptionalWarnings
      ? 'warn'
      : 'success';

  const optionalMissingSet = new Set(optionalMissingColumns);

  return (
    <div className={`col_validation_card col_validation_card_${cardStatus}`}>
      <div className={`col_validation_header col_validation_header_${cardStatus}`}>
        <div>
          <div className={`col_validation_title col_validation_title_${cardStatus}`}>
            {cardStatus === 'error' ? 'Validation failed' : 'Validation passed'}
          </div>
          <div className={`col_validation_subtitle col_validation_subtitle_${cardStatus}`}>
            {getSubtitle()}
          </div>
        </div>
      </div>

      <div className="col_validation_body">
        <div className="col_validation_columns_label">Required columns</div>
        <div className="col_validation_pills">
          {requiredColumns.map((col: string) => {
            const isFound = found.includes(col);
            return (
              <span
                key={col}
                className={`col_validation_pill ${
                  isFound
                    ? 'col_validation_pill_ok'
                    : 'col_validation_pill_missing'
                }`}
              >
                {isFound ? '✓' : '✕'} {col}
              </span>
            );
          })}
        </div>
        {missing.length > 0 && (
          <div className="col_validation_missing_hint">
            ⚠️ Missing required:{' '}
            {missing.map((col: string, i: number) => (
              <span key={col}>
                <code>{col}</code>
                {i < missing.length - 1 ? ', ' : ''}
              </span>
            ))}{' '}
            — these columns are required for the import to work.
          </div>
        )}
        {optionalColumns.length > 0 && (
          <>
            <div className="col_validation_columns_label col_validation_columns_label_optional">
              Optional columns
            </div>
            <div className="col_validation_pills">
              {optionalColumns.map((col: string) => {
                const isMissing = optionalMissingSet.has(col);
                return (
                  <span
                    key={col}
                    className={`col_validation_pill ${
                      isMissing
                        ? 'col_validation_pill_optional_missing'
                        : 'col_validation_pill_ok'
                    }`}
                  >
                    {isMissing ? '–' : '✓'} {col}
                  </span>
                );
              })}
            </div>
            {hasOptionalWarnings && (
              <div className="col_validation_optional_hint">
                The following optional columns were not included:{' '}
                {optionalMissingColumns.map((col: string, i: number) => (
                  <span key={col}>
                    <code>{col}</code>
                    {i < optionalMissingColumns.length - 1 ? ', ' : ''}
                  </span>
                ))}
                .{' '}
                <strong>
                  Existing values for these fields will not be changed.
                </strong>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default ColumnValidationCard;